import type { Role } from '@sj/shared';
import { Fragment, type ReactNode } from 'react';
import { HIDDEN_VIEWS, type NavItem } from '../../app/nav.js';
import type { IconName } from '../../components/Icon.js';

// Isi panduan per peran, istilah & tanya jawab — port dari public/js/help.js v1 (TOPICS, GLOSS, FAQ). Topik owner/admin:
// satu topik per menu (cara kelola) + `links` = menu lain yang terhubung/ikut berubah, dan topik "alur" sebagai peta besar.
// id dipakai untuk tautan /panduan?t=<id> dan hasil pencarian global. keys = kata kunci tambahan.

const MGR: Role[] = ['owner', 'admin'];
const ALL: Role[] = ['owner', 'admin', 'sales', 'driver', 'toko'];

export interface Topic {
  id: string;
  roles: Role[];
  icon: IconName;
  title: string;
  summary: string;
  keys: string;
  steps: string[];
  tips?: string[];
  go?: string;
  goLbl?: string;
  /** Keterkaitan dengan menu lain: [tujuan ('' = bukan menu admin), nama menu, apa yang terhubung]. */
  links?: [go: string, menu: string, what: string][];
  /** Tampilkan data aktif di bawah langkah (mis. skema diskon dari Produk & Harga). */
  live?: 'diskon';
}

export const TOPICS: Topic[] = [
  {
    id: 'mulai',
    roles: ALL,
    icon: 'star',
    title: 'Mulai cepat',
    summary: 'Masuk, pasang di layar utama HP, dan kenali menu.',
    keys: 'login password install pwa',
    steps: [
      'Masuk memakai **username & password** dari owner. Password sementara **wajib diganti** saat pertama masuk.',
      'Pasang ke layar utama HP: buka di Chrome → menu ⋮ → **Tambahkan ke layar utama**. Aplikasi terbuka seperti aplikasi biasa.',
      'Menu ada di samping (komputer) atau di bawah layar (HP). Menu lainnya ada di **Lainnya** / **Profil**.',
      'Tekan ikon **kaca pembesar** di atas (atau Ctrl+K di komputer) untuk mencari nota, SPO, toko, menu, atau panduan.',
      'Ganti password kapan saja di **Profil → Ganti password**.',
    ],
  },
  {
    id: 'cari',
    roles: ALL,
    icon: 'search',
    title: 'Pencarian global',
    summary: 'Cari apa saja dari satu tempat.',
    keys: 'search ctrl k cari',
    steps: [
      'Tekan ikon **kaca pembesar** di bagian atas, atau **Ctrl+K** / tombol **/** di komputer.',
      'Ketik minimal 2 huruf: nomor nota/SPO/rute/setoran, nama toko, kode atau nama produk, nama pengguna.',
      'Hasil hanya berisi data yang boleh Anda lihat (sales: data miliknya; driver: nota tugasnya). Menu & topik panduan juga ikut dicari.',
      'Pilih dengan **↑ ↓** lalu **Enter**, atau ketuk hasilnya. Nota, SPO, rute & toko langsung terbuka detailnya.',
    ],
  },

  {
    id: 'diskon',
    roles: ['owner', 'admin', 'sales', 'toko'],
    icon: 'tag',
    title: 'Aturan harga & diskon',
    summary: 'Cara harga & diskon dihitung otomatis di pesanan dan nota.',
    keys: 'diskon potongan skema harga per item bal krt zak pecahan batas tingkat kumulatif',
    live: 'diskon',
    steps: [
      '**Harga jual** diambil otomatis dari daftar harga (menu Produk & Harga) — tidak diketik di pesanan.',
      'Diskon hanya untuk produk & satuan yang **punya skema** (daftar di bawah). Produk/satuan lain **tanpa diskon**.',
      'Diskon dihitung **per item**, tidak digabung dengan varian lain. Contoh: OKM 10 bal + Segi Tiga 5 bal → OKM Rp5.000 × 10 = Rp50.000, Segi Tiga Rp2.500 × 5 = Rp12.500 → total diskon **Rp62.500**.',
      'Contoh lain: Hijau 5 bal + OKM 5 bal → masing-masing 5 bal (bukan 10) → Rp12.500 + Rp12.500 = **Rp25.000**.',
      '**Angka batas ikut**: 1–9 berarti 1 bal maupun 9 bal; 10–49 berarti 10 bal maupun 49 bal; dan seterusnya.',
      'Potongan hanya untuk **satuan utuh**: di bawah 1 bal (0,1–0,9) **tanpa diskon**; 1,5 bal → dipotong untuk 1 bal.',
      'Diskon tampil di ringkasan pesanan (**Diskon skema**) dan di kolom **Rincian** nota (mis. "10 Bal x @Rp5.000").',
      'Bila jumlah berubah (dikurangi admin saat verifikasi atau barang kurang saat serah terima), diskon & total **dihitung ulang otomatis**. Contoh: OKM 10 bal diterima 9 → turun ke tingkat 1–9 (Rp2.500/bal).',
    ],
    tips: ['Daftar skema di atas selalu sesuai yang aktif sekarang. Owner/admin mengubahnya di **Produk & Harga → Ubah skema**.'],
    links: [
      ['/produk', 'Produk & Harga', 'harga jual, divisi produk & skema diskon diatur di sini.'],
      ['', 'SPO Baru, Pesan (toko) & Verifikasi SPO', 'diskon dihitung otomatis saat pesanan dibuat & diverifikasi.'],
      ['/nota', 'Nota & Kirim', 'diskon tercetak di kolom Rincian; dihitung ulang saat serah terima.'],
    ],
  },

  // ---------- Owner & Admin: satu topik per menu (cara kelola + keterkaitan antar-menu) ----------
  {
    id: 'alur',
    roles: MGR,
    icon: 'nav',
    title: 'Peta alur antar-menu (mulai di sini)',
    summary: 'Urutan menyiapkan data & alur penjualan dari pesanan sampai uang masuk.',
    keys: 'alur sinkron terhubung integrasi urutan siapkan awal setup',
    steps: [
      '**Siapkan sekali di awal (owner):** Pengaturan (data usaha, label cetak, awalan nomor, aturan) → Data Master (satuan, divisi, area, rekening, kategori biaya, alasan) → Produk & Harga (+ skema diskon) → Pengguna & Peran (akun admin, sales, driver, toko) → Toko (area, sales, termin, titik GPS) → Stok (barang masuk awal).',
      '**Pesanan masuk:** sales membuat SPO di toko (menu sales: SPO Baru / Kunjungan) atau toko memesan sendiri (aplikasi toko) → muncul di **Verifikasi SPO**.',
      '**Nota & pengiriman:** Verifikasi SPO → **Nota & Kirim** (tab Perlu dimuat) → **Rute Kirim** (gabung nota per driver, Rekap Loading) → **Verifikasi muat** (stok berkurang di **Stok**) → driver mengantar (posisi di **Peta**) → serah terima & pembayaran.',
      '**Uang & penutupan:** pembayaran cash/transfer/tempo masuk **Keuangan** (setoran driver, konfirmasi transfer, piutang) → **Tutup Rute & Setor** di Rute Kirim → barang sisa lewat **Nota kembali** masuk lagi ke **Stok**.',
      '**Hasil:** semua nota terkirim otomatis dihitung di **Beranda**, **Rekap & Margin** (omzet, HPP, margin) dan dikurangi **Biaya** untuk laba bersih estimasi. Kunjungan sales tercatat di **Kunjungan (DKS)**.',
      'Tidak ada data yang diketik dua kali: setiap menu mengambil data dari menu sebelumnya. Bagian **Terhubung dengan menu lain** di tiap topik menjelaskan apa yang ikut berubah.',
    ],
    tips: [
      'Ubah data master di tempat asalnya saja (mis. nama area di Data Master, harga di Produk & Harga) — menu lain ikut otomatis.',
    ],
  },
  {
    id: 'beranda',
    roles: MGR,
    icon: 'home',
    title: 'Beranda',
    summary: 'Ringkasan hari ini & bulan ini, hal yang perlu ditindak.',
    go: '/beranda',
    goLbl: 'Beranda',
    keys: 'dashboard ringkasan slide banner omzet laba menu cepat',
    steps: [
      'Banner atas bergeser otomatis: omzet hari ini & bulan ini, piutang, pengiriman, stok menipis, kunjungan perlu dicek. Ketuk titiknya atau geser (HP); tahan mouse di atasnya untuk berhenti.',
      '**Menu cepat** & angka merah = jumlah pekerjaan yang menunggu (SPO, muat, transfer, stok, kunjungan).',
      '**Ringkasan bulan**: pilih bulan untuk melihat omzet, margin, biaya, laba bersih estimasi & jumlah nota. Ketuk kotak mana pun untuk membuka rinciannya.',
      'Yang tampil mengikuti hak peran (mis. tanpa HPP/margin bila tidak punya hak lihat HPP).',
    ],
    links: [
      ['/laporan', 'Rekap & Margin', 'angka omzet & margin sama persis dengan rekap pada periode yang sama.'],
      ['/biaya', 'Biaya', 'biaya operasional mengurangi laba bersih estimasi.'],
      ['/keuangan', 'Keuangan', 'piutang, cash di driver & transfer menunggu.'],
      ['/stok', 'Stok', 'daftar stok menipis (di bawah stok minimum dari Produk & Harga).'],
      ['/peta', 'Peta Driver', 'peta live ringkas driver & sales.'],
    ],
  },
  {
    id: 'operasi',
    roles: MGR,
    icon: 'list',
    title: 'Operasi (alur harian)',
    summary: 'Semua pekerjaan hari ini dalam 3 fase.',
    go: '/operasi',
    goLbl: 'Operasi',
    keys: 'dashboard fase harian',
    steps: [
      'Buka menu **Operasi**: pekerjaan hari ini dikelompokkan menjadi 3 fase beserta angka yang perlu ditindak.',
      'Fase **Sebelum berangkat**: verifikasi SPO → nota → buat Rute Kirim → cetak Rekap Loading → verifikasi muat.',
      'Fase **Di jalan**: pantau driver & sales di Peta Live; nota berpindah ke tab "Di jalan".',
      'Fase **Setelah kembali**: cek Surat Jalan Tagihan, nota kembali (retur), **Tutup Rute & Setor**, konfirmasi transfer, pantau piutang.',
      'Ketuk kotak angka mana pun untuk langsung menuju pekerjaannya.',
    ],
    links: [
      ['/spo', 'Verifikasi SPO', 'angka SPO yang siap diverifikasi.'],
      ['/nota', 'Nota & Kirim', 'nota perlu dimuat, di jalan, nota kembali & gagal kirim.'],
      ['/rute', 'Rute Kirim', 'rute hari ini & penutupannya.'],
      ['/keuangan', 'Keuangan', 'setoran & transfer yang menunggu.'],
    ],
  },
  {
    id: 'verifikasi',
    roles: MGR,
    icon: 'doc',
    title: 'Verifikasi SPO',
    summary: 'Periksa pesanan lalu terbitkan nota.',
    go: '/spo',
    goLbl: 'Verifikasi SPO',
    keys: 'spo nota ttd tolak koreksi verifikasi pesanan',
    steps: [
      'Buka **Verifikasi SPO → Perlu verifikasi**. Hanya SPO yang sudah **ditandatangani toko** yang bisa diverifikasi (yang belum ada di tab **Menunggu TTD toko**).',
      'Buka SPO, periksa produk, jumlah, harga, diskon dan stok gudang. Diskon sudah dihitung otomatis per item; bila jumlah dikurangi, diskon ikut dihitung ulang.',
      'Boleh **mengurangi** jumlah/harga. Bila menambah produk atau menaikkan jumlah/harga, SPO dikembalikan ke toko untuk **tanda tangan ulang**.',
      'Pilih tanggal kirim (dan driver bila sudah tahu) → **Terbitkan Nota** → nota (nomor dengan awalan dari Pengaturan) siap dimuat. Atau **Tolak SPO** dengan alasan (terlihat oleh sales & toko).',
    ],
    tips: ['Pesanan tempo dicek terhadap batas kredit toko.'],
    links: [
      ['', 'SPO Baru (sales) & Pesan (toko)', 'sumber SPO yang masuk ke sini.'],
      ['/produk', 'Produk & Harga', 'harga & skema diskon per item dihitung otomatis.'],
      ['/toko', 'Toko', 'termin tempo & batas kredit toko.'],
      ['/stok', 'Stok', 'stok gudang yang tersedia.'],
      ['/nota', 'Nota & Kirim', 'SPO yang diverifikasi menjadi nota di tab Perlu dimuat.'],
    ],
  },
  {
    id: 'muat',
    roles: MGR,
    icon: 'truck',
    title: 'Nota & Kirim',
    summary: 'Muat barang, serah terima, gagal kirim & nota kembali.',
    go: '/nota?tab=disiapkan',
    goLbl: 'Nota & Kirim',
    keys: 'nota muat stok keluar driver serah terima gagal kirim nota kembali retur cetak',
    steps: [
      'Tab mengikuti alur: **Perlu dimuat → Di jalan → Terkirim (nota kembali) → Selesai**, plus **Gagal** & **Batal**.',
      '**Verifikasi Muat**: buka nota → hitung barang fisik, centang tiap baris, pilih driver → **Sah — Barang Dimuat**. Stok berkurang otomatis; ditolak bila stok kurang.',
      'Serah terima dilakukan driver di HP-nya (atau admin dari detail nota): centang barang yang diturunkan, alasan bila kurang, nama penerima, pembayaran cash/transfer (pilih rekening tujuan)/tempo.',
      '**Gagal kirim**: pilih alasan (+ keterangan) → nota masuk tab Gagal → jadwalkan ulang atau batalkan.',
      '**Nota kembali**: setelah driver pulang, cocokkan nota fisik → barang sisa/ditolak otomatis masuk stok (retur).',
      '**Cetak nota**: Nota Kontan (cash) / Nota Penjualan (tempo/transfer) di kertas **F4 dibagi 3** (item sedikit) atau **dibagi 2** (item banyak) — otomatis, atau tetap 1/3 / 1/2 F4 di Pengaturan → Cetak nota. Dikelompokkan per divisi (Total 1, Total 2), dengan rekening transfer & catatan kaki.',
      '**Titipan**: toko bayar sebagian sebelum barang dikirim → buka nota → **Catat titipan**. Saat serah terima driver hanya menagih sisanya.',
    ],
    links: [
      ['/spo', 'Verifikasi SPO', 'asal nota.'],
      ['/rute', 'Rute Kirim', 'nota digabung per driver menjadi satu kali jalan.'],
      ['/stok', 'Stok', 'stok keluar saat muat, masuk lagi saat nota kembali (retur).'],
      ['/keuangan', 'Keuangan', 'pembayaran saat antar & pelunasan; transfer menunggu konfirmasi.'],
      ['/master', 'Data Master', 'alasan gagal kirim, alasan barang kurang, rekening tujuan transfer.'],
      ['/pengaturan', 'Pengaturan', 'judul & label cetak nota, data usaha, catatan kaki, awalan nomor.'],
    ],
  },
  {
    id: 'rute',
    roles: MGR,
    icon: 'nav',
    title: 'Rute Kirim, Rekap Loading & Surat Jalan Tagihan',
    summary: 'Gabungkan nota satu driver menjadi satu kali jalan, lalu tutup & setor.',
    go: '/rute',
    goLbl: 'Rute Kirim',
    keys: 'rekapitulasi loading muat urutan antar surat jalan tagihan tutup rute setor',
    steps: [
      '**Buat Rute Kirim**: pilih driver, tanggal kirim, area (dari Data Master, opsional), lalu centang nota "Perlu dimuat". Urutan daftar = urutan antar.',
      'Di detail rute, atur urutan antar dengan **▲ ▼**. Urutan muat otomatis kebalikannya (toko terakhir dimuat paling dalam).',
      '**Rekapitulasi** (Rekap Loading): total barang per toko × produk, kolom dikelompokkan sesuai kelompok & label rekap produk.',
      '**Surat Jalan Nota Tagihan** (F4 mendatar): rekap per toko — STS (C kontan · FK tempo · T transfer · R retur), Quantity **S** (Selai) / **O** (Other), Tgl TTPN & **Jumlah Titipan**, Jumlah Tagihan (= nota − titipan), Realisasi, Sisa, inisial sales, keterangan.',
      'Setelah semua nota terkirim/gagal: **Tutup Rute & Setor** → isi uang yang diterima; selisih **wajib** diberi keterangan.',
    ],
    links: [
      ['/nota', 'Nota & Kirim', 'nota yang dimasukkan ke rute & status antarnya.'],
      ['/pengguna', 'Pengguna', 'daftar driver; inisial sales tampil di kolom SLS.'],
      ['/produk', 'Produk & Harga', 'label & kelompok rekap untuk kolom Rekapitulasi.'],
      ['/master', 'Data Master', 'pilihan area rute.'],
      ['/keuangan', 'Keuangan', 'Tutup Rute & Setor membuat setoran (STR-) dan menghapus cash di tangan driver.'],
      ['/peta', 'Peta Driver', 'rute rencana & posisi driver saat jalan.'],
    ],
  },
  {
    id: 'peta',
    roles: MGR,
    icon: 'map',
    title: 'Peta Driver & Sales',
    summary: 'Pantau posisi pengiriman dan kunjungan sales.',
    go: '/peta',
    goLbl: 'Peta',
    keys: 'gps lokasi live tracking riwayat',
    steps: [
      '**Beranda** menampilkan peta ringkas posisi driver (ikon truk) dan sales (ikon orang) yang sedang aktif.',
      'Menu **Peta** menampilkan rute rencana & tujuan driver, jejak hari ini, dan **rute kunjungan sales** (titik bernomor: hijau = order, biru = tidak order, merah = tutup).',
      '"**Sinyal hilang**" berarti tidak ada kiriman GPS lebih dari 5 menit (HP terkunci atau tanpa sinyal).',
      'Riwayat: pilih driver/sales & tanggal lalu **Tampilkan rute**.',
    ],
    links: [
      ['', 'Pengiriman (driver) & Kunjungan (sales)', 'sumber posisi GPS: Mulai Perjalanan / Mulai kunjungan.'],
      ['/toko', 'Toko', 'titik GPS toko sebagai tujuan.'],
      ['/pengaturan', 'Pengaturan', 'lokasi gudang (pusat peta), lama simpan jejak GPS, kecepatan perkiraan tiba.'],
    ],
  },
  {
    id: 'keuangan',
    roles: MGR,
    icon: 'wallet',
    title: 'Keuangan: setoran, transfer & piutang',
    summary: 'Kendalikan uang cash, transfer dan tempo.',
    go: '/keuangan',
    goLbl: 'Keuangan',
    keys: 'setoran transfer piutang tempo jatuh tempo pelunasan umur rekening',
    steps: [
      '**Setoran driver**: cash yang masih dipegang tiap driver; terima uangnya → nomor setoran & selisih tercatat.',
      '**Konfirmasi transfer**: periksa bukti & rekening tujuan, lalu sahkan atau tolak (tagihan kembali terbuka).',
      '**Piutang / tempo**: nota belum lunas, dikelompokkan menurut umur (batas hari diatur di Pengaturan). Catat pelunasan dari detail nota → **Catat pembayaran** (pilih rekening bila transfer).',
      '**Riwayat pembayaran**: semua pembayaran pada rentang tanggal; bisa diunduh CSV.',
    ],
    links: [
      ['/nota', 'Nota & Kirim', 'pembayaran saat antar & pelunasan mengubah status bayar nota.'],
      ['/rute', 'Rute Kirim', 'Tutup Rute & Setor menerima cash driver satu rute.'],
      ['/master', 'Data Master', 'rekening tujuan transfer.'],
      ['/pengaturan', 'Pengaturan', 'batas umur piutang & termin tempo bawaan.'],
      ['', 'Aplikasi toko', 'toko melihat tagihan & rekening transfer di Pesanan Saya.'],
    ],
  },
  {
    id: 'dks-admin',
    roles: MGR,
    icon: 'pin',
    title: 'Kunjungan (DKS): rekap, target & pemeriksaan',
    summary: 'Pantau CB, EC, NOO dan kunjungan janggal.',
    go: '/kunjungan',
    goLbl: 'Kunjungan',
    keys: 'cb ec noo target pencapaian daftar kunjungan salesman perlu dicek',
    steps: [
      'Pilih tanggal/sales untuk melihat **CB** (jumlah kunjungan), **EC** (kunjungan yang order), **NOO** (toko yang order pertama kali) dan **Qty**.',
      'Tekan **Set target** per sales per hari → pencapaian % tampil otomatis.',
      'Tiap kunjungan mencatat jam, jarak ke toko, **foto dari kamera** (ber-cap jam & lokasi) dan siapa yang ditemui. Ketuk kunjungan untuk melihat fotonya. Tekan **Cetak DKS** untuk arsip.',
      'Tombol **Perlu dicek** (juga angka di lonceng): kunjungan janggal — GPS lemah, lokasi tidak dibagikan, pindah toko terlalu cepat, check-in dari titik yang sama, atau **dibantah toko**. Periksa lalu tekan **Sudah dicek**.',
      'Kolom **Via app**: toko yang pesan sendiri & minta tidak dikunjungi — ikut dihitung di pencapaian CB/EC agar sales tidak dirugikan.',
    ],
    links: [
      ['', 'Kunjungan (sales)', 'check-in, foto & alasan dicatat sales di HP.'],
      ['/spo', 'Verifikasi SPO', 'SPO yang dibuat di toko dihitung sebagai kunjungan efektif (EC).'],
      ['/toko', 'Toko', 'titik GPS toko untuk radius check-in; sales penanggung jawab.'],
      ['/master', 'Data Master', 'pilihan alasan tidak order.'],
      [
        '/pengaturan',
        'Pengaturan',
        'radius check-in, batas akurasi GPS, kecepatan, batas konfirmasi toko, rentang "Perlu dicek", judul & TTD cetak DKS.',
      ],
    ],
  },
  {
    id: 'toko-admin',
    roles: MGR,
    icon: 'store',
    title: 'Toko / client',
    summary: 'Data toko, sales penanggung jawab, termin & batas kredit, titik GPS.',
    go: '/toko',
    goLbl: 'Toko',
    keys: 'toko client pelanggan termin kredit limit titik gps area sales akun toko piutang',
    steps: [
      '**+ Toko**: nama, pemilik, telepon, alamat, **area** (pilihan dari Data Master), kode (kosong = otomatis dengan awalan dari Pengaturan).',
      'Atur **sales penanggung jawab**, **termin tempo** (hari; 0 = tidak boleh tempo) dan **batas kredit** (0 = tanpa batas).',
      '**Titik GPS**: isi dari lokasi saat berada di toko. Titik yang sudah ada hanya bisa dipindah oleh peran dengan hak pindah titik (mencegah check-in palsu).',
      'Ketuk toko untuk melihat piutang, order terakhir, akun login toko & 30 nota terakhir. Toko tidak dihapus — **nonaktifkan** bila berhenti.',
    ],
    links: [
      ['/master', 'Data Master', 'pilihan area (ganti nama area ikut ke semua toko).'],
      ['/pengguna', 'Pengguna', 'akun toko (login aplikasi toko) dihubungkan ke satu toko; sales penanggung jawab.'],
      ['/spo', 'Verifikasi SPO', 'termin & batas kredit dicek saat pesanan tempo.'],
      ['/kunjungan', 'Kunjungan', 'titik GPS dipakai untuk check-in sales.'],
      ['/keuangan', 'Keuangan', 'piutang toko.'],
      ['/laporan', 'Rekap & Margin', 'rekap per toko & per area.'],
    ],
  },
  {
    id: 'laporan',
    roles: MGR,
    icon: 'chart',
    title: 'Rekap & Margin',
    summary: 'Omzet, margin dan pertumbuhan otomatis.',
    go: '/laporan',
    goLbl: 'Rekap & Margin',
    keys: 'omzet margin hpp csv excel pertumbuhan laporan',
    steps: [
      'Pilih rentang tanggal dan kelompok **harian / mingguan / bulanan**.',
      'Omzet = barang **diterima toko** × harga − diskon. Margin = omzet − HPP saat muat.',
      'Lihat rincian per produk, toko, sales, driver, metode bayar atau area.',
      'Tekan **Unduh CSV rekap / rincian** untuk dibuka di Excel.',
    ],
    links: [
      ['/nota', 'Nota & Kirim', 'hanya nota terkirim/selesai yang dihitung.'],
      ['/stok', 'Stok', 'HPP rata-rata dari barang masuk.'],
      ['/master', 'Data Master', 'area untuk rekap per area.'],
      ['/beranda', 'Beranda', 'ringkasan yang sama di halaman awal.'],
    ],
  },
  {
    id: 'stok',
    roles: MGR,
    icon: 'box',
    title: 'Stok: barang masuk, opname & kartu stok',
    summary: 'Stok berubah hanya lewat transaksi.',
    go: '/stok',
    goLbl: 'Stok',
    keys: 'hpp opname kartu stok minimum barang masuk sumber penyesuaian',
    steps: [
      '**Barang masuk**: pilih sumber (dari Data Master), isi produk, jumlah & HPP per satuan → HPP rata-rata diperbarui otomatis.',
      '**Stok opname**: masukkan stok fisik, pilih alasan (dari Data Master) + keterangan → selisih dibuat otomatis.',
      '**Kartu stok**: riwayat masuk/keluar/retur/penyesuaian per produk; bisa diunduh CSV.',
      'Stok tidak bisa minus dan tidak bisa diubah langsung — selalu lewat transaksi agar terlacak.',
    ],
    links: [
      ['/nota', 'Nota & Kirim', 'stok keluar saat Verifikasi Muat; retur masuk saat Nota kembali.'],
      ['/produk', 'Produk & Harga', 'stok minimum → peringatan stok menipis di Beranda.'],
      ['/master', 'Data Master', 'sumber barang masuk & alasan penyesuaian stok.'],
      ['/laporan', 'Rekap & Margin', 'HPP dipakai menghitung margin.'],
      ['/pengaturan', 'Pengaturan', 'awalan nomor barang masuk & penyesuaian.'],
    ],
  },
  {
    id: 'biaya',
    roles: MGR,
    icon: 'receipt',
    title: 'Biaya operasional',
    summary: 'Catat bensin, gaji, sewa & biaya lain.',
    go: '/biaya',
    goLbl: 'Biaya',
    keys: 'biaya pengeluaran operasional bensin gaji sewa kategori laba',
    steps: [
      '**+ Biaya**: tanggal, kategori (dari Data Master), nominal & keterangan.',
      'Pilih rentang tanggal untuk melihat total per kategori.',
      'Salah catat? Hapus (dengan konfirmasi) lalu catat ulang — tercatat di log aktivitas.',
    ],
    links: [
      ['/master', 'Data Master', 'pilihan kategori biaya (ganti nama ikut ke biaya lama).'],
      ['/beranda', 'Beranda', 'biaya bulan ini & laba bersih estimasi (margin − biaya).'],
    ],
  },
  {
    id: 'produk',
    roles: MGR,
    icon: 'tag',
    title: 'Produk & Harga + skema diskon',
    summary: 'Daftar produk, harga, satuan, divisi & diskon bertingkat.',
    go: '/produk',
    goLbl: 'Produk & Harga',
    keys: 'produk harga satuan kedua krt bal divisi diskon bertingkat kemasan label rekap kelompok stok minimum',
    steps: [
      '**+ Produk**: kode, nama, **satuan** (dari Data Master), harga jual, stok minimum.',
      '**Satuan kedua** (opsional, mis. Krt): harga sendiri & isi dalam satuan dasar — stok tetap dihitung dalam satuan dasar.',
      '**Foto produk** (maks. 3: tampak depan, luar/kemasan, dalam/isi): buka produk yang sudah tersimpan → ketuk kotak foto → pilih foto (boleh dari galeri). Foto tampil di menu Pesan toko & SPO Baru sales; ketuk gambar di keranjang untuk melihat semua foto.',
      '**Divisi** (dari Data Master) menentukan skema diskon & pengelompokan di nota (Total 1, Total 2). **Kemasan** tampil di kolom Rincian nota. **Label & kelompok rekap** untuk kolom Rekapitulasi.',
      '**Skema diskon**: diatur per divisi & satuan, tetapi dihitung **per item** — qty tiap produk sendiri, tidak digabung varian lain (mis. OKM 10 bal → Rp5.000/bal, Segi Tiga 5 bal → Rp2.500/bal). Angka batas ikut (1–9, 10–49, …); potongan hanya untuk satuan **utuh**, di bawah 1 bal tanpa diskon. Produk/satuan tanpa skema tidak mendapat diskon. Rincian & contoh: topik **Aturan harga & diskon**.',
      '**Ubah skema** untuk menambah/mengubah tingkat: pilih divisi & satuan, isi **jumlah minimal** tiap tingkat dan **potongan per satuan** (isi 0 untuk tingkat tanpa potongan, mis. 1–4 krt). Batas atas tingkat = jumlah minimal tingkat berikutnya − 1.',
      'Kenaikan harga cukup diubah di sini — pesanan baru langsung memakai harga baru.',
      'Produk tidak dihapus — **nonaktifkan** agar tidak bisa dipesan; stok diubah lewat menu Stok.',
    ],
    links: [
      ['/master', 'Data Master', 'pilihan satuan (+ langkah jumlah), divisi & kelompok rekap.'],
      ['', 'SPO Baru, Pesan (toko) & Verifikasi SPO', 'harga & diskon dihitung otomatis dari sini.'],
      ['/nota', 'Nota & Kirim', 'kolom Rincian (kemasan/diskon) & kelompok per divisi di cetak nota.'],
      ['/rute', 'Rute Kirim', 'label & kelompok kolom Rekapitulasi.'],
      ['/stok', 'Stok', 'stok minimum & HPP.'],
    ],
  },
  {
    id: 'data-master',
    roles: MGR,
    icon: 'list',
    title: 'Data Master (daftar pilihan)',
    summary: 'Satuan, divisi, kelompok rekap, area, rekening, kategori biaya & daftar alasan.',
    go: '/master',
    goLbl: 'Data Master',
    keys: 'data master satuan pcs bal krt divisi area rekening bank kategori biaya alasan sumber barang kelompok rekap urutan',
    steps: [
      'Semua pilihan di form diatur di sini — satu tempat, tidak ada daftar yang diketik ulang di menu lain.',
      'Pilih tab jenisnya → **+ Tambah**. Satuan punya isian langkah jumlah (mis. pcs: tambah 10, ± 5); rekening diisi bank, nomor & atas nama.',
      'Atur posisi dengan **▲ ▼** — urutan ini dipakai di semua pilihan form; **satuan paling atas** menjadi satuan bawaan produk baru.',
      '**Ganti nama** ikut mengganti data yang memakainya (satuan, divisi, kelompok rekap, area, kategori biaya). Alasan (kunjungan, gagal kirim, dll.) adalah catatan riwayat dan tidak ditulis ulang.',
      'Pilihan yang sudah dipakai tidak bisa dihapus — **Nonaktifkan** agar tidak muncul lagi tanpa mengubah riwayat; bisa diaktifkan kembali.',
    ],
    tips: ['Hak "Kelola Data Master" bawaan hanya owner; bisa diberikan ke peran admin lewat Peran & Akses.'],
    links: [
      ['/produk', 'Produk & Harga', 'satuan, divisi, kelompok rekap; skema diskon memakai divisi & satuan.'],
      ['/toko', 'Toko', 'area toko.'],
      ['/rute', 'Rute Kirim', 'area rute.'],
      ['/biaya', 'Biaya', 'kategori biaya.'],
      ['/stok', 'Stok', 'sumber barang masuk & alasan penyesuaian.'],
      ['/nota', 'Nota & Kirim', 'alasan gagal kirim, alasan barang kurang, rekening tujuan transfer & rekening di cetak nota.'],
      ['/kunjungan', 'Kunjungan', 'alasan tidak order (dipilih sales).'],
      ['', 'Aplikasi toko', 'rekening transfer & langkah jumlah satuan di menu Pesan.'],
    ],
  },
  {
    id: 'pengguna',
    roles: ['owner'],
    icon: 'users',
    title: 'Pengguna & akun toko',
    summary: 'Kelola akun admin, sales, driver dan toko.',
    go: '/pengguna',
    goLbl: 'Pengguna',
    keys: 'akun user nonaktif password reset inisial akun toko',
    steps: [
      '**+ Pengguna**: username, nama, **peran**, HP, **inisial** (tercetak di nota & surat jalan, mis. YAP) dan password sementara — wajib diganti saat masuk pertama.',
      'Akun toko wajib dihubungkan ke satu toko agar toko bisa memesan, menyetujui order & melihat tagihannya sendiri.',
      'Reset password: isi password sementara baru. Akun tidak dihapus, cukup **dinonaktifkan** — riwayat tetap utuh dan sesinya langsung keluar.',
    ],
    links: [
      ['/peran', 'Peran & Akses', 'peran menentukan menu & aksi akun.'],
      ['/toko', 'Toko', 'akun toko ↔ toko; sales penanggung jawab toko.'],
      ['/rute', 'Rute Kirim', 'daftar driver; inisial sales di Surat Jalan Tagihan.'],
      ['/nota', 'Nota & Kirim', 'nama & telepon salesman serta inisial admin di cetak nota.'],
      ['/kunjungan', 'Kunjungan', 'target & rekap per sales.'],
    ],
  },
  {
    id: 'peran',
    roles: ['owner'],
    icon: 'shield',
    title: 'Peran & Akses',
    summary: 'Atur menu & aksi yang boleh dipakai tiap peran.',
    go: '/peran',
    goLbl: 'Peran & Akses',
    keys: 'peran role hak akses izin rbac kasir gudang custom',
    steps: [
      'Menu **Peran & Akses** → **+ Peran** → isi nama (mis. Kasir), pilih **jenis dasar**: Admin (semua data), Sales (hanya tokonya) atau Driver (hanya nota tugasnya). Bisa juga salin dari peran lain.',
      'Centang hak per menu: Lihat / Buat / Ubah / Hapus dan aksi khusus (verifikasi SPO, konfirmasi transfer, lihat HPP, kelola Data Master, dst.). Hak yang dibutuhkan ikut tercentang otomatis.',
      'Di **Pengguna**, pilih peran untuk tiap akun. Perubahan hak berlaku langsung (tanpa login ulang) dan tercatat di log aktivitas.',
      'Peran bawaan Admin, Sales & Driver bisa diubah haknya; Owner & Toko terkunci. Peran yang masih dipakai akun tidak bisa dihapus.',
    ],
    tips: ['Pengguna, Peran & Akses dan Pengaturan hanya untuk owner — tidak bisa diberikan ke peran lain.'],
    links: [
      ['/pengguna', 'Pengguna', 'setiap akun memakai satu peran.'],
      ['', 'Semua menu', 'menu, tombol & angka di Beranda mengikuti hak peran.'],
    ],
  },
  {
    id: 'pengaturan',
    roles: ['owner'],
    icon: 'settings',
    title: 'Pengaturan & log aktivitas',
    summary: 'Data usaha, label cetak, penomoran, aturan & ambang.',
    go: '/pengaturan',
    goLbl: 'Pengaturan',
    keys: 'pengaturan data usaha alamat telepon label cetak awalan nomor prefix radius gps ambang piutang log aktivitas',
    steps: [
      'Pengaturan dibagi **per tab**; tiap tab punya tombol **Simpan** sendiri dan hanya menyimpan isian di tab itu — simpan sebelum pindah tab.',
      '**Usaha**: nama usaha, telepon & keterangannya, alamat 2 baris; **lokasi gudang** (pusat peta & titik berangkat).',
      '**Penjualan & aturan**: termin tempo bawaan; aturan kunjungan sales (radius check-in, akurasi GPS, batas kecepatan, batas konfirmasi toko, masa permintaan kunjungan, rentang "Perlu dicek"); ambang & perhitungan (radius driver tiba, jarak toko terdekat, batas umur piutang, kecepatan & faktor jalan untuk perkiraan tiba); lama simpan jejak GPS.',
      '**Dokumen & cetak**: ukuran kertas nota, catatan kaki nota (satu baris per catatan), judul & label dokumen cetak (nota cash/tempo, kolom produk, label telepon kantor, kolom tanda tangan), dan **awalan nomor** SPO, nota, rute, setoran, barang masuk, penyesuaian & kode toko (huruf besar/angka, tidak boleh sama).',
      '**Kontak bantuan**: penyedia aplikasi yang tampil di Panduan.',
      '**Log aktivitas**: mencatat setiap aksi penting (siapa, kapan, apa).',
    ],
    tips: ['Daftar pilihan (satuan, area, rekening, kategori, alasan) tidak ada di sini — semuanya di Data Master.'],
    links: [
      ['/nota', 'Nota & Kirim', 'kop, label, catatan kaki & awalan nomor nota.'],
      ['/rute', 'Rute Kirim', 'judul & tanda tangan Rekapitulasi dan Surat Jalan Tagihan.'],
      ['/kunjungan', 'Kunjungan', 'aturan anti kunjungan fiktif & cetak DKS.'],
      ['/keuangan', 'Keuangan', 'batas umur piutang, termin tempo bawaan.'],
      ['/peta', 'Peta Driver', 'lokasi gudang, lama simpan GPS, perkiraan tiba.'],
      ['/toko', 'Toko', 'awalan kode toko otomatis.'],
    ],
  },

  // ---------- Sales ----------
  {
    id: 'spo-baru',
    roles: ['sales'],
    icon: 'docplus',
    title: 'Membuat SPO di toko',
    summary: 'Catat pesanan & minta tanda tangan toko.',
    go: '/spo-baru',
    goLbl: 'SPO Baru',
    keys: 'pesanan order ttd tanda tangan',
    steps: [
      '**SPO Baru** → pilih toko (diurutkan dari yang terdekat dengan posisi Anda) atau **Toko baru**.',
      'Ketuk produk untuk menambah, atur jumlahnya. Harga otomatis dari daftar harga; **diskon skema dihitung otomatis per item** dan tampil di ringkasan (lihat **Aturan harga & diskon**).',
      'Pilih cara bayar: **Cash**, **Transfer** atau **Tempo** (sesuai termin toko).',
      '**Bukti kunjungan**: pilih **Saya di toko**, ambil **foto dari kamera** (depan toko / rak) dan pilih siapa yang ditemui. GPS harus aktif dan Anda harus di lokasi toko.',
      'Pesanan dari telepon: pilih **Order lewat telepon** — tidak dihitung kunjungan, toko menyetujui lewat aplikasinya.',
      'Persetujuan toko: serahkan HP ke pemilik toko untuk **tanda tangan**, atau kirim ke aplikasi toko (bila toko punya akun).',
      'Tekan **Kirim SPO ke Admin**. SPO di toko otomatis tercatat sebagai kunjungan efektif (EC).',
    ],
    tips: ['Mengubah isi pesanan setelah ditandatangani akan menghapus TTD — minta toko tanda tangan ulang.'],
  },
  {
    id: 'dks-sales',
    roles: ['sales'],
    icon: 'pin',
    title: 'Kunjungan harian (DKS) & GPS',
    summary: 'Bagikan lokasi dan catat setiap kunjungan.',
    go: '/dks',
    goLbl: 'Kunjungan',
    keys: 'gps lokasi check-in kunjungi tidak order tutup cb ec',
    steps: [
      'Di menu **Kunjungan**, tekan **Mulai kunjungan** saat mulai berkeliling — lokasi Anda dibagikan ke admin secara real-time. Biarkan layar menyala.',
      'Toko berlabel **Minta dikunjungi** (paling atas) meminta Anda datang. Toko berlabel **Tidak perlu dikunjungi** sudah pesan sendiri — tidak perlu didatangi sampai pesanannya diterima, dan tidak mengurangi pencapaian Anda.',
      'Tombol **Rute** di tiap toko: peta **2 pilihan rute** (tercepat & alternatif) dari posisi Anda ke toko, lengkap jarak & waktu, atau buka di Google Maps.',
      'Toko yang order: tekan **Pesan** (buat SPO).',
      'Toko yang tidak order / tutup: tekan **Kunjungi**, pilih alasan, **ambil foto dari kamera** dan pilih siapa yang ditemui (Pemilik / Penjaga / Tidak ada orang). TTD opsional. Check-in hanya bisa **di lokasi toko**.',
      'Toko akan diminta mengonfirmasi kunjungan Anda lewat aplikasinya. Kunjungan janggal diperiksa owner.',
      'Tekan **Selesai kunjungan** saat pulang agar lokasi berhenti dibagikan.',
      'Angka **CB / EC** di atas menunjukkan pencapaian hari ini.',
    ],
  },
  {
    id: 'spo-status',
    roles: ['sales'],
    icon: 'doc',
    title: 'Status SPO, toko & rekap saya',
    summary: 'Pantau pesanan dan toko Anda.',
    go: '/spo',
    goLbl: 'SPO Saya',
    keys: 'ditolak menunggu ttd toko saya rekap',
    steps: [
      '**SPO Saya**: Menunggu TTD toko, Menunggu admin, Jadi nota, atau Ditolak (lihat alasannya).',
      '**Toko Saya**: tambah/ubah toko dan simpan titik GPS toko saat Anda berada di lokasi.',
      '**Rekap Saya**: penjualan Anda per periode.',
    ],
  },

  // ---------- Driver ----------
  {
    id: 'drv-muat',
    roles: ['driver'],
    icon: 'box',
    title: 'Hari Ini & checklist muat barang',
    summary: 'Ikuti langkah: Muat → Berangkat → Antar → Setor.',
    go: '/muat',
    goLbl: 'Muat',
    keys: 'muat jumlah beda checklist hari ini langkah',
    steps: [
      'Menu **Hari Ini** menampilkan 4 langkah kerja (Muat → Berangkat → Antar → Setor) beserta yang perlu dikerjakan sekarang.',
      'Setelah gudang memuat, buka menu **Muat**.',
      'Hitung dan centang setiap barang. Urutan muat dimulai dari toko yang **diantar terakhir**.',
      'Jumlah tidak sama? Tekan **Jumlah beda** — selisih dilaporkan ke admin. Perbaiki muatan lalu centang lagi.',
      'Tombol **Mulai Perjalanan** (di bawah checklist) aktif setelah semua barang tercentang.',
    ],
  },
  {
    id: 'drv-jalan',
    roles: ['driver'],
    icon: 'nav',
    title: 'Perjalanan & navigasi',
    summary: 'GPS aktif selama mengantar.',
    go: '/antar',
    goLbl: 'Antar',
    keys: 'gps navigasi mulai selesai perjalanan rute',
    steps: [
      'Di menu **Muat** tekan **Mulai Perjalanan** dan izinkan akses lokasi. Posisi dikirim ke kantor selama aplikasi terbuka (di menu mana pun); layar dijaga menyala.',
      'Menu **Antar** menampilkan petunjuk arah ke toko berikutnya, plus **rute alternatif** (garis putus-putus abu-abu, waktu tempuhnya tertulis di bawah jam tiba); tekan **Navigasi** untuk membuka Google Maps.',
      'Tekan **Selesai Perjalanan** di menu Antar setelah semua tujuan selesai.',
    ],
  },
  {
    id: 'drv-serah',
    roles: ['driver'],
    icon: 'check',
    title: 'Serah terima & pembayaran',
    summary: 'Turunkan barang, terima pembayaran, setor uang.',
    go: '/antar',
    goLbl: 'Antar',
    keys: 'terkirim cash transfer tempo gagal kurang kirim',
    steps: [
      'Di menu **Antar**, pada toko tujuan tekan **Turunkan & Terkirim**, lalu centang setiap barang yang diturunkan.',
      'Bila kurang (ditolak/rusak), kurangi jumlahnya dan pilih alasannya — sisa dibawa pulang. Diskon & total nota **dihitung ulang otomatis**; tagih sesuai total yang tampil di layar.',
      'Isi nama penerima dan pilih pembayaran: **Cash** (boleh sebagian, sisanya piutang), **Transfer** (foto bukti), atau **Tempo**.',
      'Toko tutup/menolak: tekan **Gagal** dan pilih alasannya.',
      'Uang cash di tangan & nota yang selesai ada di menu **Setoran**; setorkan ke admin setelah kembali.',
    ],
  },

  // ---------- Toko ----------
  {
    id: 'toko-pesan',
    roles: ['toko'],
    icon: 'bag',
    title: 'Memesan sendiri',
    summary: 'Pesan langsung ke distributor.',
    go: '/pesan',
    goLbl: 'Pesan',
    keys: 'order pesan',
    steps: [
      'Menu **Pesan** → pilih produk & jumlah → pilih cara bayar → kirim.',
      'Harga & diskon dihitung otomatis: potongan per satuan tampil di tiap produk, totalnya di **Diskon skema**. Diskon dihitung per produk (lihat **Aturan harga & diskon**).',
      'Setelah terkirim, pilih apakah toko **perlu dikunjungi sales**. Pilih **Tidak perlu** bila pesanan sudah cukup — sales tidak datang sampai pesanan Anda terima.',
      'Pesanan dari aplikasi toko otomatis tercatat disetujui toko.',
    ],
  },
  {
    id: 'toko-kunjungan',
    roles: ['toko'],
    icon: 'shield',
    title: 'Konfirmasi kunjungan sales',
    summary: 'Pastikan sales benar-benar datang.',
    go: '/toko-home',
    goLbl: 'Beranda',
    keys: 'kunjungan sales konfirmasi benar tidak benar',
    steps: [
      'Setiap kunjungan sales ke toko Anda muncul di **Beranda → Konfirmasi kunjungan sales** (juga angka di lonceng).',
      'Tekan **Benar** bila sales memang datang, atau **Tidak benar** bila tidak ada sales yang datang — tulis keterangannya. Laporan langsung diteruskan ke pemilik usaha.',
    ],
  },
  {
    id: 'toko-ttd',
    roles: ['toko'],
    icon: 'check',
    title: 'Menyetujui pesanan dari sales',
    summary: 'Tanda tangani atau tolak pesanan.',
    go: '/pesanan',
    goLbl: 'Pesanan Saya',
    keys: 'tanda tangan setujui tolak',
    steps: [
      'Pesanan yang dibuat sales untuk toko Anda muncul untuk disetujui.',
      'Periksa isi pesanan, lalu tekan **Setujui & Tanda Tangan** — atau tolak dengan alasan.',
    ],
  },
  {
    id: 'toko-tagihan',
    roles: ['toko'],
    icon: 'wallet',
    title: 'Tagihan & lacak kiriman',
    summary: 'Lihat tagihan dan posisi driver.',
    go: '/pesanan?tab=tagihan',
    goLbl: 'Tagihan',
    keys: 'tagihan lunas jatuh tempo lacak driver',
    steps: [
      '**Pesanan Saya → Tagihan**: nota yang belum lunas dan jatuh temponya.',
      '**Lacak Kiriman**: posisi driver menuju toko Anda dan perkiraan tiba.',
    ],
  },
];

export interface Gloss {
  term: string;
  def: string;
  roles?: Role[];
}

export const GLOSS: Gloss[] = [
  {
    term: 'SPO',
    def: 'Surat Pesanan Order — pesanan toko yang dibuat sales (atau toko sendiri); nomor berawalan sesuai Pengaturan (bawaan SPO-).',
  },
  { term: 'TTD digital', def: 'Tanda tangan toko di HP sebagai bukti toko menyetujui isi pesanan, disertai kode verifikasi.' },
  { term: 'Nota / Surat Jalan', def: 'Dokumen tagihan per toko (awalan bawaan NJ-) yang dibawa driver saat mengantar.' },
  { term: 'Rute Kirim', def: 'Kumpulan nota untuk satu driver dalam satu kali jalan (awalan bawaan RK-).' },
  { term: 'Rekap Loading', def: 'Total barang yang harus dimuat untuk satu rute (matriks toko × produk).' },
  { term: 'Surat Jalan Tagihan', def: 'Rekap tagihan per toko dalam satu rute untuk dicocokkan dengan uang driver.' },
  { term: 'Setoran', def: 'Penyerahan uang cash driver ke kantor (awalan bawaan STR-); selisih tercatat.' },
  {
    term: 'DKS',
    def: 'Daftar Kunjungan Salesman — catatan kunjungan sales ke toko lengkap dengan GPS, foto kamera & yang ditemui.',
  },
  { term: 'CB', def: 'Call — jumlah kunjungan sales.' },
  { term: 'EC', def: 'Effective Call — kunjungan yang menghasilkan order.' },
  { term: 'NOO', def: 'New Open Outlet — toko yang order pertama kali pada periode tersebut.' },
  {
    term: 'Skema diskon',
    def: 'Potongan harga bertingkat menurut jumlah beli, dihitung per item (tidak kumulatif) dan hanya untuk satuan utuh. Daftar aktif ada di topik Aturan harga & diskon.',
  },
  { term: 'Tempo', def: 'Bayar belakangan sesuai termin toko; tercatat sebagai piutang sampai lunas.' },
  { term: 'Retur', def: 'Barang yang kembali ke gudang (sisa/ditolak) dan otomatis menambah stok.' },
  { term: 'HPP & Margin', roles: MGR, def: 'HPP = harga pokok rata-rata saat barang dimuat; margin = omzet − HPP.' },
  {
    term: 'Data Master',
    roles: MGR,
    def: 'Daftar pilihan yang dipakai semua form (satuan, divisi, kelompok rekap, area, rekening, kategori biaya, alasan) — diatur di satu menu.',
  },
  {
    term: 'STS',
    roles: MGR,
    def: 'Kode status di Surat Jalan Tagihan: C = kontan (cash), FK = faktur kredit (tempo), T = transfer, R = retur.',
  },
];

export interface Faq {
  q: string;
  a: string;
  roles?: Role[];
}

export const FAQ: Faq[] = [
  {
    roles: ['owner', 'admin', 'sales', 'driver'],
    q: 'GPS tidak terkirim / muncul "Sinyal hilang"?',
    a: 'Pastikan GPS HP aktif, izin lokasi untuk browser diizinkan, aplikasi dibuka lewat alamat https, dan halaman tetap terbuka (jangan kunci layar terlalu lama). Pelacakan saat layar mati membutuhkan aplikasi Android (add-on).',
  },
  {
    roles: ['owner', 'admin', 'sales'],
    q: 'Check-in kunjungan ditolak karena terlalu jauh?',
    a: 'Check-in harus dilakukan dekat titik toko (radius diatur owner di Pengaturan). Bila titik toko salah, minta admin memperbaiki lokasi toko.',
  },
  {
    roles: ['owner', 'admin', 'sales'],
    q: 'SPO tidak bisa diverifikasi?',
    a: 'SPO harus sudah ditandatangani toko. Lihat tab "Menunggu TTD toko" dan minta toko menandatangani (di HP sales atau lewat aplikasi toko).',
  },
  {
    roles: ['owner', 'admin', 'sales', 'toko'],
    q: 'Kenapa pesanan tidak mendapat diskon / diskonnya lebih kecil?',
    a: 'Diskon dihitung per item, bukan gabungan varian: 5 bal Hijau + 5 bal OKM dihitung masing-masing 5 bal. Jumlah di bawah 1 bal tidak dapat diskon, dan produk/satuan tanpa skema (lihat Aturan harga & diskon) memang tanpa potongan.',
  },
  {
    roles: MGR,
    q: 'Verifikasi muat ditolak karena stok kurang?',
    a: 'Catat barang masuk terlebih dahulu di menu Stok, atau kurangi jumlah pada pesanan.',
  },
  {
    roles: ['driver'],
    q: 'Tombol "Mulai Perjalanan" tidak aktif?',
    a: 'Semua barang di checklist muat harus dicentang dulu. Bila jumlah beda, laporkan lewat "Jumlah beda" dan minta gudang membetulkan muatan.',
  },
  {
    roles: ALL,
    q: 'Lupa password?',
    a: 'Hubungi owner/admin untuk mengatur ulang password. Anda wajib menggantinya saat masuk berikutnya.',
  },
  {
    roles: MGR,
    q: 'Apakah data bisa diekspor?',
    a: 'Ya. Setiap laporan memiliki tombol Unduh CSV, dan seluruh data adalah milik usaha Anda.',
  },
  {
    roles: ALL,
    q: 'Tampilan tidak berubah setelah ada pembaruan?',
    a: 'Muat ulang halaman (tarik layar ke bawah di HP) atau tutup lalu buka kembali aplikasi.',
  },
];

export const isMgrRole = (role: Role) => MGR.includes(role);
export const forRole = (x: { roles?: Role[] }, role: Role) => !x.roles || x.roles.includes(role);
export const norm = (s: unknown) => String(s || '').toLowerCase();
export const topicsFor = (role: Role) => TOPICS.filter((t) => forRole(t, role));
/** Teks topik untuk saringan & pencarian (judul, ringkasan, kata kunci, langkah, tips, keterkaitan). */
export const topicText = (t: Topic) =>
  norm([t.title, t.summary, t.keys, ...t.steps, ...(t.tips || []), ...(t.links || []).map((l) => `${l[1]} ${l[2]}`)].join(' '));

/** Tujuan tombol "Buka …" hanya tampil bila halamannya boleh dibuka (nav = menu pengguna ini, lihat navFor). */
export const canGo = (nav: NavItem[], go: string | undefined) => {
  const view = String(go || '')
    .replace(/^#?\//, '')
    .split(/[?/]/)[0] as string;
  return nav.some((n) => n[0] === view) || HIDDEN_VIEWS.includes(view);
};

/**
 * Nomor WA (wa.me) dari nomor telepon — sama dengan waLink() di help.js v1 (kosong bila tanpa angka). Beda dengan
 * waLink di lib/device.ts (toko.js v1: tetap 'https://wa.me/' bila nilai tidak kosong tetapi tanpa angka).
 */
export const waLink = (phone: unknown) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d ? `https://wa.me/${d.startsWith('0') ? `62${d.slice(1)}` : d}` : '';
};

/** **tebal** di teks panduan → <b> (v1 fmt()); satu potongan teks = satu text node. */
export function Fmt({ s }: { s: string }): ReactNode {
  return s.split(/\*\*(.+?)\*\*/g).map((p, i) => (i % 2 ? <b key={i}>{p}</b> : p ? <Fragment key={i}>{p}</Fragment> : null));
}
